import { DateFilter } from '@chiroup/core/types/AdminMetrics.type';
import * as Tabs from '@radix-ui/react-tabs';
import { AnimatePresence, motion } from 'framer-motion';
import {
  AlertTriangle,
  BarChart3,
  Briefcase,
  Calculator,
  Calendar,
  CheckCircle,
  Clock,
  Cloud,
  DollarSign,
  Loader2,
  PieChart,
  RotateCcw,
  Server,
  Shield,
  Table,
  Target,
  TrendingUp,
  Trophy,
  User,
  X,
  Zap,
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useQuery } from 'react-query';
import { cn } from '../../lib/utils';
import { convertFilterToDateRange } from '../../utils/dateUtils';
import AnalyticsLoading from './components/AnalyticsLoading';
import DashboardNavigation from './components/DashboardNavigation';
import MetricInfoTooltip from './components/MetricInfoTooltip';
import ModernDatePicker from './components/ModernDatePicker';
import { SalesTrendLineChart } from './components/SalesTrendLineChart';
import analyticsService from './services/analytics.service';

// Pure functions moved outside component to prevent recreation on every render
const formatNumber = (num: number): string => {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`;
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;

  return num.toLocaleString();
};

const formatCurrency = (num: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
};

// Helper function to convert date string to Date object
const getDateValue = (dateString?: string): Date | null => {
  if (!dateString) return null;

  return new Date(dateString);
};

// Helper function to get current time for live data indicator
const getCurrentTime = (): string => {
  const now = new Date();

  return now.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  });
};

// Helper function to get relevant icon for each metric
const getMetricIcon = (metricId: string) => {
  if (metricId.includes('new-sales-opportunities')) return Briefcase;
  if (metricId.includes('new-demos')) return Calendar;
  if (metricId.includes('follow-up-demos')) return RotateCcw;
  if (metricId.includes('closed-sales')) return CheckCircle;
  if (metricId.includes('close-rate')) return TrendingUp;
  if (metricId.includes('sales-cycle')) return Clock;
  if (metricId.includes('contract-value') || metricId.includes('deal-size')) return DollarSign;
  if (metricId.includes('trizetto')) return Server;
  if (metricId.includes('ncmic')) return Shield;
  if (metricId.includes('practisync')) return Cloud;

  return Calculator; // Default icon
};

// Helper function to get colorful background for each metric icon
const getMetricIconBackground = (metricId: string) => {
  if (metricId.includes('new-sales-opportunities')) return 'bg-orange-100';
  if (metricId.includes('new-demos')) return 'bg-blue-100';
  if (metricId.includes('follow-up-demos')) return 'bg-cyan-100';
  if (metricId.includes('closed-sales')) return 'bg-green-100';
  if (metricId.includes('close-rate')) return 'bg-purple-100';
  if (metricId.includes('sales-cycle')) return 'bg-indigo-100';
  if (metricId.includes('contract-value')) return 'bg-emerald-100';
  if (metricId.includes('deal-size')) return 'bg-teal-100';
  if (metricId.includes('trizetto')) return 'bg-blue-100';
  if (metricId.includes('ncmic')) return 'bg-yellow-100';
  if (metricId.includes('practisync')) return 'bg-pink-100';

  return 'bg-gray-100'; // Default background
};

// Helper function to get light row hover background for each metric
const getMetricRowHoverBackground = (metricId: string) => {
  if (metricId.includes('new-sales-opportunities')) {
    return 'hover:bg-gradient-to-r hover:from-orange-50/60 hover:via-orange-25/30 hover:to-orange-50/60';
  }

  if (metricId.includes('new-demos')) {
    return 'hover:bg-gradient-to-r hover:from-blue-50/60 hover:via-blue-25/30 hover:to-blue-50/60';
  }

  if (metricId.includes('follow-up-demos')) {
    return 'hover:bg-gradient-to-r hover:from-cyan-50/60 hover:via-cyan-25/30 hover:to-cyan-50/60';
  }

  if (metricId.includes('closed-sales')) {
    return 'hover:bg-gradient-to-r hover:from-green-50/60 hover:via-green-25/30 hover:to-green-50/60';
  }

  if (metricId.includes('close-rate')) {
    return 'hover:bg-gradient-to-r hover:from-purple-50/60 hover:via-purple-25/30 hover:to-purple-50/60';
  }

  if (metricId.includes('sales-cycle')) {
    return 'hover:bg-gradient-to-r hover:from-indigo-50/60 hover:via-indigo-25/30 hover:to-indigo-50/60';
  }

  if (metricId.includes('contract-value')) {
    return 'hover:bg-gradient-to-r hover:from-emerald-50/60 hover:via-emerald-25/30 hover:to-emerald-50/60';
  }

  if (metricId.includes('deal-size')) {
    return 'hover:bg-gradient-to-r hover:from-teal-50/60 hover:via-teal-25/30 hover:to-teal-50/60';
  }

  if (metricId.includes('trizetto')) {
    return 'hover:bg-gradient-to-r hover:from-blue-50/60 hover:via-blue-25/30 hover:to-blue-50/60';
  }

  if (metricId.includes('ncmic')) {
    return 'hover:bg-gradient-to-r hover:from-yellow-50/60 hover:via-yellow-25/30 hover:to-yellow-50/60';
  }

  if (metricId.includes('practisync')) {
    return 'hover:bg-gradient-to-r hover:from-pink-50/60 hover:via-pink-25/30 hover:to-pink-50/60';
  }

  return 'hover:bg-gradient-to-r hover:from-gray-50/60 hover:via-gray-25/30 hover:to-gray-50/60'; // Default background
};

// Helper function to get cell hover background for each metric
const getMetricCellHoverBackground = (metricId: string) => {
  if (metricId.includes('new-sales-opportunities')) return 'from-orange-100/40 to-orange-200/40';
  if (metricId.includes('new-demos')) return 'from-blue-100/40 to-blue-200/40';
  if (metricId.includes('follow-up-demos')) return 'from-cyan-100/40 to-cyan-200/40';
  if (metricId.includes('closed-sales')) return 'from-green-100/40 to-green-200/40';
  if (metricId.includes('close-rate')) return 'from-purple-100/40 to-purple-200/40';
  if (metricId.includes('sales-cycle')) return 'from-indigo-100/40 to-indigo-200/40';
  if (metricId.includes('contract-value')) return 'from-emerald-100/40 to-emerald-200/40';
  if (metricId.includes('deal-size')) return 'from-teal-100/40 to-teal-200/40';
  if (metricId.includes('trizetto')) return 'from-blue-100/40 to-blue-200/40';
  if (metricId.includes('ncmic')) return 'from-yellow-100/40 to-yellow-200/40';
  if (metricId.includes('practisync')) return 'from-pink-100/40 to-pink-200/40';

  return 'from-gray-100/40 to-gray-200/40'; // Default background
};

// Helper function to get matching border color for each metric
const getMetricBorderColor = (metricId: string) => {
  if (metricId.includes('new-sales-opportunities')) return 'hover:border-l-orange-400';
  if (metricId.includes('new-demos')) return 'hover:border-l-blue-400';
  if (metricId.includes('follow-up-demos')) return 'hover:border-l-cyan-400';
  if (metricId.includes('closed-sales')) return 'hover:border-l-green-400';
  if (metricId.includes('close-rate')) return 'hover:border-l-purple-400';
  if (metricId.includes('sales-cycle')) return 'hover:border-l-indigo-400';
  if (metricId.includes('contract-value')) return 'hover:border-l-emerald-400';
  if (metricId.includes('deal-size')) return 'hover:border-l-teal-400';
  if (metricId.includes('trizetto')) return 'hover:border-l-blue-400';
  if (metricId.includes('ncmic')) return 'hover:border-l-yellow-400';
  if (metricId.includes('practisync')) return 'hover:border-l-pink-400';

  return 'hover:border-l-gray-400'; // Default border
};

// Helper function to get icon color for each metric
const getMetricIconColor = (metricId: string) => {
  if (metricId.includes('new-sales-opportunities')) return 'text-orange-600';
  if (metricId.includes('new-demos')) return 'text-blue-600';
  if (metricId.includes('follow-up-demos')) return 'text-cyan-600';
  if (metricId.includes('closed-sales')) return 'text-green-600';
  if (metricId.includes('close-rate')) return 'text-purple-600';
  if (metricId.includes('sales-cycle')) return 'text-indigo-600';
  if (metricId.includes('contract-value')) return 'text-emerald-600';
  if (metricId.includes('deal-size')) return 'text-teal-600';
  if (metricId.includes('trizetto')) return 'text-blue-600';
  if (metricId.includes('ncmic')) return 'text-yellow-600';
  if (metricId.includes('practisync')) return 'text-pink-600';

  return 'text-gray-600'; // Default color
};

// ── Sales Activity-Points Tracker (Monday 12085034544) ─────────────────────────
type ActivityRole = 'manager' | 'bdr';

interface ActivityRepConfig {
  name: string;
  userId: string;
  email: string;
  role: ActivityRole;
  dailyGoal: number;
}

// Manager daily goal = 50 (per story). BDR daily goal is a placeholder pending
// confirmation (the story only specified the manager goal).
const ACTIVITY_REPS: ActivityRepConfig[] = [
  {
    name: 'Erika Roland',
    userId: '005Qj00000cJYUjIAO',
    email: 'e.roland@chiroup.com',
    role: 'manager',
    dailyGoal: 50,
  },
  {
    name: 'Becki Wollenburg',
    userId: '0054x000002RabuAAC',
    email: 'becki@chiroup.com',
    role: 'manager',
    dailyGoal: 50,
  },
  {
    name: 'Curt Langel',
    userId: '005Qj0000092esDIAQ',
    email: 'c.langel@chiroup.com',
    role: 'manager',
    dailyGoal: 50,
  },
  {
    name: 'Devan Multidor',
    userId: '005Qj00000myjOPIAY',
    email: 'd.multidor@chiroup.com',
    role: 'bdr',
    dailyGoal: 30,
  },
  {
    name: 'Rachel Obrien',
    userId: '005Qj00000sqpoxIAA',
    email: 'r.obrien@chiroup.com',
    role: 'bdr',
    dailyGoal: 30,
  },
];

const ACTIVITY_WEIGHTS: Record<
  ActivityRole,
  { demo10: number; demo5: number; call: number; email: number; text: number }
> = {
  manager: { demo10: 10, demo5: 5, call: 2.5, email: 2.5, text: 1 },
  bdr: { demo10: 0, demo5: 0, call: 1, email: 1, text: 1 },
};

const ACTIVITY_METRIC_IDS = {
  call: 'sales-activity-outbound-calls-leaderboard',
  text: 'sales-activity-texts-leaderboard',
  email: 'sales-activity-emails-leaderboard',
  demo10: 'sales-activity-demos-10pt-leaderboard',
  demo5: 'sales-activity-demos-5pt-leaderboard',
} as const;

// Count Mon–Fri days in an inclusive [start, end] date range (YYYY-MM-DD strings).
const businessDaysBetween = (startStr?: string, endStr?: string): number => {
  if (!startStr || !endStr) return 1;
  const start = new Date(`${startStr}T00:00:00`);
  const end = new Date(`${endStr}T00:00:00`);

  if (isNaN(start.getTime()) || isNaN(end.getTime()) || end < start) return 1;

  let count = 0;
  const cursor = new Date(start);
  while (cursor <= end) {
    const day = cursor.getDay();
    if (day !== 0 && day !== 6) count++;
    cursor.setDate(cursor.getDate() + 1);
  }

  return Math.max(1, count);
};

// Number cell styling — zeros are muted so real activity stands out.
const activityCellClass = (value: number): string =>
  cn(
    'px-3 py-3 text-center text-sm tabular-nums',
    value === 0 ? 'text-gray-300' : 'text-slate-700'
  );

const SalesDashboard = () => {
  const [dateFilter, setDateFilter] = useState<DateFilter>({
    type: 'monthToDate',
  });
  const [minimumDurationComplete, setMinimumDurationComplete] = useState(false);
  const [showAccuracyBanner, setShowAccuracyBanner] = useState(true);
  const [activeTab, setActiveTab] = useState('table');

  const { data, isLoading, error, refetch, isFetching } = useQuery(
    ['analytics-metrics', 'sales', dateFilter],
    () => analyticsService.getMetrics('sales', dateFilter),
    {
      refetchOnWindowFocus: false,
      staleTime: 2 * 60 * 1000, // 2 minutes
      keepPreviousData: true,
    }
  );

  // Activity Tracker tab — lazy: only queries Salesforce once the tab is opened.
  const activityQuery = useQuery(
    ['analytics-metrics', 'sales-activity', dateFilter],
    () => analyticsService.getMetrics('sales-activity', dateFilter),
    {
      refetchOnWindowFocus: false,
      staleTime: 2 * 60 * 1000, // 2 minutes
      keepPreviousData: true,
      enabled: activeTab === 'activity',
    }
  );

  // Auto-hide banner after 14 seconds, but only after loading is complete
  useEffect(() => {
    // Only start timer when loading is done and minimum duration is complete
    if (!isLoading && minimumDurationComplete && showAccuracyBanner) {
      const timer = setTimeout(() => {
        setShowAccuracyBanner(false);
      }, 14000); // 14 seconds

      return () => clearTimeout(timer);
    }
  }, [isLoading, minimumDurationComplete, showAccuracyBanner]);

  // Prepare metrics data before any conditional returns to follow Rules of Hooks
  const allMetrics = data?.metrics || [];

  // Convert metrics array to object for direct access instead of O(n) find operations
  const metricsMap = useMemo(() => {
    const map: Record<string, number> = {};
    allMetrics.forEach(metric => {
      if (typeof metric?.value === 'number') {
        map[metric.id] = metric.value;
      }
    });

    return map;
  }, [allMetrics]);

  // Activity Tracker rows: per-rep counts → weighted points → sorted by points.
  const activityRows = useMemo(() => {
    const metrics = activityQuery.data?.metrics || [];
    const countFor = (metricId: string, repName: string): number => {
      const metric = metrics.find(m => m.id === metricId);
      const value = metric?.value as Record<string, number> | undefined;

      return value?.[repName] || 0;
    };

    return ACTIVITY_REPS.map(rep => {
      const counts = {
        call: countFor(ACTIVITY_METRIC_IDS.call, rep.userId),
        text: countFor(ACTIVITY_METRIC_IDS.text, rep.userId),
        email: countFor(ACTIVITY_METRIC_IDS.email, rep.email),
        demo10: countFor(ACTIVITY_METRIC_IDS.demo10, rep.userId),
        demo5: countFor(ACTIVITY_METRIC_IDS.demo5, rep.userId),
      };
      const weights = ACTIVITY_WEIGHTS[rep.role];
      const points =
        counts.call * weights.call +
        counts.text * weights.text +
        counts.email * weights.email +
        counts.demo10 * weights.demo10 +
        counts.demo5 * weights.demo5;

      return { ...rep, counts, points: Math.round(points * 10) / 10 };
    }).sort((a, b) => b.points - a.points);
  }, [activityQuery.data]);

  // Scale the daily goal by the number of business days in the selected range, so
  // Status/Goal are meaningful for multi-day views (a daily target on "Today", a
  // monthly target on "Month to Date", etc.). Custom uses its own start/end dates.
  const goalBusinessDays = useMemo(() => {
    const range =
      dateFilter.type === 'custom'
        ? { startDate: dateFilter.startDate, endDate: dateFilter.endDate }
        : convertFilterToDateRange(dateFilter.type);

    return businessDaysBetween(range.startDate, range.endDate);
  }, [dateFilter]);

  // Renders one role-scoped activity table. Managers show demo columns; BDRs
  // don't (they have no demos). Neither shows a Role column — the section
  // heading conveys the role.
  const renderActivityTable = (title: string, rows: typeof activityRows, showDemos: boolean) => {
    if (rows.length === 0) return null;

    const activityMetricFor = (metricId: string) =>
      activityQuery.data?.metrics.find(m => m.id === metricId);

    return (
      <div className="mb-8">
        <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <span
            className={cn(
              'inline-block w-2 h-2 rounded-full',
              showDemos ? 'bg-blue-500' : 'bg-purple-500'
            )}
          />
          {title}
          <span className="text-gray-400 font-normal">· {rows.length}</span>
        </h3>
        <div className="bg-white rounded-2xl border border-gray-200/60 shadow-xl overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-gradient-to-r from-gray-50 via-white to-gray-50 border-b border-gray-200 text-xs uppercase tracking-wide text-gray-600">
                <th className="px-4 py-3 text-left">Rep</th>
                <th className="px-3 py-3 text-center">
                  <span className="inline-flex items-center gap-1">
                    Calls
                    <MetricInfoTooltip
                      info={activityMetricFor(ACTIVITY_METRIC_IDS.call)?.info}
                      query={activityMetricFor(ACTIVITY_METRIC_IDS.call)?.query}
                    />
                  </span>
                </th>
                <th className="px-3 py-3 text-center">
                  <span className="inline-flex items-center gap-1">
                    Texts
                    <MetricInfoTooltip
                      info={activityMetricFor(ACTIVITY_METRIC_IDS.text)?.info}
                      query={activityMetricFor(ACTIVITY_METRIC_IDS.text)?.query}
                    />
                  </span>
                </th>
                <th className="px-3 py-3 text-center">
                  <span className="inline-flex items-center gap-1">
                    Emails
                    <MetricInfoTooltip
                      info={activityMetricFor(ACTIVITY_METRIC_IDS.email)?.info}
                      query={activityMetricFor(ACTIVITY_METRIC_IDS.email)?.query}
                    />
                  </span>
                </th>
                {showDemos && (
                  <th className="px-3 py-3 text-center">
                    <span className="inline-flex items-center gap-1">
                      Demos
                      <MetricInfoTooltip
                        info={activityMetricFor(ACTIVITY_METRIC_IDS.demo10)?.info}
                        query={activityMetricFor(ACTIVITY_METRIC_IDS.demo10)?.query}
                      />
                    </span>
                  </th>
                )}
                {showDemos && (
                  <th className="px-3 py-3 text-center">
                    <span className="inline-flex items-center gap-1">
                      Followup Demos
                      <MetricInfoTooltip
                        info={activityMetricFor(ACTIVITY_METRIC_IDS.demo5)?.info}
                        query={activityMetricFor(ACTIVITY_METRIC_IDS.demo5)?.query}
                      />
                    </span>
                  </th>
                )}
                <th className="px-3 py-3 text-center">Points</th>
                <th className="px-3 py-3 text-center">Goal</th>
                <th className="px-3 py-3 text-center">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((row, index) => {
                const rangeGoal = row.dailyGoal * goalBusinessDays;
                const met = row.points >= rangeGoal;
                const pct =
                  rangeGoal > 0 ? Math.min(999, Math.round((row.points / rangeGoal) * 100)) : 0;
                const rankClass =
                  index === 0
                    ? 'bg-amber-100 text-amber-700'
                    : index === 1
                      ? 'bg-slate-200 text-slate-600'
                      : index === 2
                        ? 'bg-orange-100 text-orange-700'
                        : 'bg-gray-100 text-gray-400';
                const barColor = met
                  ? 'bg-emerald-500'
                  : pct >= 50
                    ? 'bg-amber-500'
                    : 'bg-rose-500';
                const labelColor = met
                  ? 'text-emerald-700'
                  : pct >= 50
                    ? 'text-amber-700'
                    : 'text-rose-600';

                return (
                  <tr key={row.name} className="hover:bg-gray-50 transition-colors duration-200">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <span
                          className={cn(
                            'flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold',
                            rankClass
                          )}
                        >
                          {index + 1}
                        </span>
                        <span className="text-sm font-semibold text-slate-800">{row.name}</span>
                      </div>
                    </td>
                    <td className={activityCellClass(row.counts.call)}>{row.counts.call}</td>
                    <td className={activityCellClass(row.counts.text)}>{row.counts.text}</td>
                    <td className={activityCellClass(row.counts.email)}>{row.counts.email}</td>
                    {showDemos && (
                      <td className={activityCellClass(row.counts.demo10)}>{row.counts.demo10}</td>
                    )}
                    {showDemos && (
                      <td className={activityCellClass(row.counts.demo5)}>{row.counts.demo5}</td>
                    )}
                    <td className="px-3 py-3 text-center text-base font-bold text-slate-900 tabular-nums">
                      {row.points}
                    </td>
                    <td className="px-3 py-3 text-center text-sm text-slate-400 tabular-nums">
                      {rangeGoal}
                    </td>
                    <td className="px-3 py-3">
                      <div className="flex items-center justify-center gap-2">
                        <div className="w-16 h-1.5 rounded-full bg-gray-100 overflow-hidden">
                          <div
                            className={cn('h-full rounded-full', barColor)}
                            style={{ width: `${Math.min(100, pct)}%` }}
                          />
                        </div>
                        <span className={cn('text-xs font-semibold whitespace-nowrap', labelColor)}>
                          {met ? 'On track' : `${pct}%`}
                        </span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  // Optimized metric value getter - O(1) lookup instead of O(n) find
  const getMetricValue = (id: string): number => {
    return metricsMap[id] ?? 0;
  };

  // Show full loading screen for initial load - only hide when both data is loaded AND minimum duration is complete
  const shouldShowLoading = isLoading || !minimumDurationComplete;

  if (shouldShowLoading) {
    return <AnalyticsLoading onMinimumDurationComplete={() => setMinimumDurationComplete(true)} />;
  }

  // Show loading overlay for filter changes (but not for initial load)
  const showLoadingOverlay = isFetching && !isLoading;

  // Get all leaderboard metrics (scalable for multiple metrics)
  const leaderboardMetrics = allMetrics.filter(
    metric => metric.displayType === ('leaderboard' as any)
  );

  // Get all unique sales reps from all leaderboard metrics
  const allSalesReps = new Set<string>();
  leaderboardMetrics.forEach(metric => {
    if (metric.value && typeof metric.value === 'object') {
      Object.keys(metric.value as { [key: string]: number }).forEach(rep => {
        allSalesReps.add(rep);
      });
    }
  });

  // Define a consistent list of all known sales reps to always show
  // This ensures columns don't disappear when filtering to short periods
  // Only including actual sales reps that exist in the system
  const knownSalesReps = ['Becki Wollenburg', 'Curt Langel', 'Erika Roland'];

  // Filter to only show the known sales reps (no others)
  const filteredSalesReps = Array.from(allSalesReps).filter(rep => knownSalesReps.includes(rep));

  // Combine filtered reps with known reps to ensure all three always show
  const combinedSalesReps = new Set([...knownSalesReps, ...filteredSalesReps]);

  // Sort sales reps by total performance (sum of all metrics)
  const sortedSalesReps = Array.from(combinedSalesReps).sort((a, b) => {
    const totalA = leaderboardMetrics.reduce((sum, metric) => {
      const metricValue = metric.value as { [key: string]: number } | undefined;

      return sum + (metricValue?.[a] || 0);
    }, 0);
    const totalB = leaderboardMetrics.reduce((sum, metric) => {
      const metricValue = metric.value as { [key: string]: number } | undefined;

      return sum + (metricValue?.[b] || 0);
    }, 0);

    return totalB - totalA; // Descending order
  });

  const dateOptions = [
    { value: 'today', label: 'Today' },
    { value: 'past7days', label: 'Past 7 Days' },
    { value: 'monthToDate', label: 'Month to Date' },
    { value: 'past90days', label: 'Past 90 Days' },
    { value: 'yearToDate', label: 'Year to Date' },
    { value: 'custom', label: 'Custom' },
  ] as const;

  const handleFilterChange = (type: DateFilter['type']) => {
    if (type === 'custom') {
      setDateFilter({
        type,
        startDate: dateFilter.startDate || new Date().toISOString().split('T')[0],
        endDate: dateFilter.endDate || new Date().toISOString().split('T')[0],
      });
    } else {
      setDateFilter({ type });
    }
  };

  const handleDateChange = (field: 'startDate' | 'endDate', date: Date | null) => {
    if (date) {
      const dateString = date.toISOString().split('T')[0];
      setDateFilter({
        ...dateFilter,
        [field]: dateString,
      });
    }
  };

  if (error) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-md p-4 mt-4">
          <div className="flex">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Unable to load sales metrics</h3>
              <div className="mt-2 text-sm text-red-700">
                {error instanceof Error ? error.message : 'An error occurred'}
              </div>
              <div className="mt-3">
                <button
                  onClick={() => refetch()}
                  className="bg-red-100 hover:bg-red-200 text-red-800 px-3 py-1 rounded text-sm font-medium"
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative">
      <DashboardNavigation currentDashboardId="sales" />

      {/* Loading overlay for filter changes */}
      {showLoadingOverlay && <AnalyticsLoading />}

      <div className="p-6">
        {/* Enhanced Filter Section - ORGS1 Style */}
        <div className="mb-8">
          <motion.div
            className="bg-gradient-to-r from-emerald-600 via-emerald-700 to-green-700 rounded-2xl p-6 shadow-xl"
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
              {/* Title Section */}
              <motion.div
                className="flex flex-col gap-4"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2, duration: 0.6 }}
              >
                {/* Title and Icon Row */}
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-white/20 rounded-lg backdrop-blur-sm">
                    <Trophy className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-white">Sales Performance Metrics</h2>
                    <p className="text-emerald-100 text-sm">Track your sales team's performance</p>
                  </div>
                </div>

                {/* Live Data Indicator - Moved down and aligned */}
                <motion.div
                  className="flex items-center gap-2 px-3 py-1.5 bg-white/10 backdrop-blur-sm rounded-lg border border-white/20 self-start ml-14"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.8, duration: 0.5 }}
                >
                  {/* Animated Pulse Dot */}
                  <div className="relative flex items-center">
                    <motion.div
                      className="w-2 h-2 bg-emerald-400 rounded-full"
                      animate={{
                        scale: [1, 1.2, 1],
                        opacity: [0.8, 1, 0.8],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: 'easeInOut',
                      }}
                    />
                    {/* Ripple Effect */}
                    <motion.div
                      className="absolute inset-0 bg-emerald-400 rounded-full"
                      animate={{
                        scale: [1, 2.5],
                        opacity: [0.6, 0],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: 'easeOut',
                      }}
                    />
                  </div>

                  {/* Live Data Text */}
                  <div className="flex items-center">
                    <motion.span
                      className="text-xs font-bold tracking-wider text-white drop-shadow-sm"
                      animate={{ opacity: [0.9, 1, 0.9] }}
                      transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                    >
                      LIVE DATA
                    </motion.span>
                    <span className="text-white/80 mx-1 text-xs">•</span>
                    <motion.span
                      className="text-xs font-medium text-white/90 tracking-wide"
                      key={getCurrentTime()} // Re-trigger animation on time update
                      initial={{ opacity: 0, y: 2 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3 }}
                    >
                      Updated {getCurrentTime()}
                    </motion.span>
                  </div>
                </motion.div>
              </motion.div>

              {/* Enhanced Date Range Selector */}
              <motion.div
                className="bg-white/10 backdrop-blur-sm rounded-xl p-3 border border-white/20"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.4, duration: 0.6 }}
              >
                {/* Filter Buttons */}
                <div className="flex flex-wrap gap-2 justify-center">
                  {dateOptions.map(option => (
                    <motion.button
                      key={option.value}
                      onClick={() => handleFilterChange(option.value)}
                      className={cn(
                        'px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-300 whitespace-nowrap',
                        'border focus:outline-none focus:ring-2 focus:ring-emerald-400',
                        dateFilter.type === option.value
                          ? 'bg-white text-emerald-700 hover:bg-white/95 shadow-md border-emerald-200'
                          : 'text-white hover:bg-white/20 hover:text-emerald-100 border-white/20'
                      )}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      {option.label}
                    </motion.button>
                  ))}
                </div>

                {/* Advanced Custom Date Pickers */}
                <AnimatePresence>
                  {dateFilter.type === 'custom' && (
                    <motion.div
                      className="mt-4 pt-4 border-t border-white/20"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3, ease: 'easeInOut' }}
                    >
                      <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-white/90 font-medium">Start Date</span>
                          <ModernDatePicker
                            selected={getDateValue(dateFilter.startDate)}
                            onChange={date => handleDateChange('startDate', date)}
                            placeholder="MM/DD/YYYY"
                            maxDate={new Date()}
                            align="start"
                            calendarType="start-date"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-white/90 font-medium">End Date</span>
                          <ModernDatePicker
                            selected={getDateValue(dateFilter.endDate)}
                            onChange={date => handleDateChange('endDate', date)}
                            placeholder="MM/DD/YYYY"
                            minDate={getDateValue(dateFilter.startDate) || undefined}
                            maxDate={new Date()}
                            align="start"
                            calendarType="end-date"
                          />
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            </div>
          </motion.div>
        </div>

        {/* Professional Data Accuracy Toast - Bottom Left */}
        <AnimatePresence>
          {showAccuracyBanner && (
            <motion.div
              initial={{ opacity: 0, x: -100, y: 20, scale: 0.9 }}
              animate={{ opacity: 1, x: 0, y: 0, scale: 1 }}
              exit={{ opacity: 0, x: -100, y: 20, scale: 0.9 }}
              transition={{
                duration: 0.7,
                ease: [0.16, 1, 0.3, 1],
                type: 'spring',
                stiffness: 120,
                damping: 18,
              }}
              className="fixed bottom-6 left-6 z-50 max-w-lg"
            >
              <div className="relative group">
                {/* Enhanced glow effect */}
                <div className="absolute -inset-2 bg-gradient-to-r from-blue-500/20 via-indigo-500/20 to-slate-500/20 rounded-3xl blur-xl opacity-60 group-hover:opacity-80 transition-opacity duration-500"></div>
                <div className="absolute -inset-1 bg-gradient-to-r from-blue-400/30 via-indigo-400/30 to-slate-400/30 rounded-2xl blur-sm opacity-40"></div>

                {/* Main toast container */}
                <div className="relative bg-gradient-to-br from-white via-slate-50/80 to-blue-50/60 border border-slate-200/80 rounded-2xl shadow-2xl backdrop-blur-2xl overflow-hidden">
                  {/* Sophisticated pattern overlay */}
                  <div className="absolute inset-0 bg-gradient-to-br from-white/40 via-transparent to-blue-50/20 rounded-2xl"></div>

                  {/* Subtle animated background pattern */}
                  <motion.div
                    className="absolute inset-0 opacity-5"
                    style={{
                      backgroundImage: `radial-gradient(circle at 20% 50%, rgba(59, 130, 246, 0.3) 0%, transparent 50%), 
                                       radial-gradient(circle at 80% 20%, rgba(99, 102, 241, 0.3) 0%, transparent 50%),
                                       radial-gradient(circle at 40% 80%, rgba(71, 85, 105, 0.3) 0%, transparent 50%)`,
                    }}
                    animate={{
                      backgroundPosition: ['0% 0%', '100% 100%'],
                    }}
                    transition={{
                      duration: 20,
                      repeat: Infinity,
                      repeatType: 'reverse',
                      ease: 'linear',
                    }}
                  />

                  <div className="relative p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-start space-x-4">
                        <div className="flex-shrink-0">
                          <motion.div
                            className="w-12 h-12 bg-gradient-to-br from-blue-500 via-indigo-500 to-slate-600 rounded-2xl flex items-center justify-center shadow-xl"
                            whileHover={{ scale: 1.05, rotate: 5 }}
                            transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                          >
                            <AlertTriangle className="h-6 w-6 text-white drop-shadow-sm" />
                          </motion.div>
                        </div>
                        <div className="flex-1 pt-0.5">
                          <motion.h4
                            className="text-base font-bold text-slate-900 mb-3 flex items-center"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.2, duration: 0.5 }}
                          >
                            <span className="bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                              Data Accuracy Notice
                            </span>
                            <motion.div
                              className="ml-3 w-2.5 h-2.5 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full shadow-sm"
                              animate={{
                                scale: [1, 1.2, 1],
                                opacity: [0.7, 1, 0.7],
                              }}
                              transition={{
                                duration: 2,
                                repeat: Infinity,
                                ease: 'easeInOut',
                              }}
                            />
                          </motion.h4>
                          <motion.p
                            className="text-sm text-slate-700 leading-relaxed font-medium"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.3, duration: 0.5 }}
                          >
                            Please note that{' '}
                            <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-blue-100 text-blue-800 font-semibold text-xs border border-blue-200">
                              EHR Total New ACV
                            </span>{' '}
                            and{' '}
                            <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-indigo-100 text-indigo-800 font-semibold text-xs border border-indigo-200">
                              EHR Average New ACV
                            </span>{' '}
                            metrics are estimated amounts. These values may not be 100% accurate as
                            subscription amounts are not consistently tracked in Salesforce. Use
                            these metrics as approximate indicators for analysis.
                          </motion.p>
                        </div>
                      </div>
                      <motion.button
                        onClick={() => setShowAccuracyBanner(false)}
                        className="flex-shrink-0 ml-4 p-2.5 rounded-xl text-slate-400 hover:text-slate-600 hover:bg-white/80 transition-all duration-300 hover:scale-110 hover:shadow-md border border-transparent hover:border-slate-200"
                        aria-label="Dismiss toast"
                        whileHover={{ rotate: 90 }}
                        whileTap={{ scale: 0.95 }}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.4, duration: 0.3 }}
                      >
                        <X className="h-4 w-4" />
                      </motion.button>
                    </div>

                    {/* Enhanced progress bar for auto-dismiss */}
                    <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-slate-100/50 rounded-b-2xl overflow-hidden">
                      <motion.div
                        className="h-full bg-gradient-to-r from-blue-500 via-indigo-500 to-slate-500 shadow-sm"
                        initial={{ width: '100%' }}
                        animate={{ width: '0%' }}
                        transition={{ duration: 14, ease: 'linear' }}
                      />
                      <motion.div
                        className="absolute inset-0 bg-gradient-to-r from-white/30 to-transparent"
                        animate={{
                          x: ['-100%', '100%'],
                        }}
                        transition={{
                          duration: 2,
                          repeat: Infinity,
                          ease: 'linear',
                        }}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Tab Navigation - Stripe Dashboard Style */}
        <Tabs.Root value={activeTab} onValueChange={setActiveTab}>
          <Tabs.List className="grid w-full grid-cols-1 sm:grid-cols-3 gap-3 p-2 bg-gradient-to-r from-slate-50/50 to-gray-50/80 backdrop-blur-sm rounded-3xl border border-white/20 shadow-2xl max-w-2xl mx-auto mb-8">
            <Tabs.Trigger
              value="table"
              className="group relative flex items-center justify-center px-6 py-4 rounded-2xl font-semibold text-sm transition-all duration-500 ease-in-out transform data-[state=active]:bg-gradient-to-br data-[state=active]:from-emerald-500 data-[state=active]:via-green-500 data-[state=active]:to-teal-600 data-[state=active]:text-white data-[state=active]:shadow-2xl data-[state=active]:scale-105 data-[state=inactive]:bg-white/60 data-[state=inactive]:text-gray-600 data-[state=inactive]:hover:bg-white/80 data-[state=inactive]:hover:scale-102 data-[state=inactive]:hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:ring-offset-2 focus:ring-offset-transparent"
            >
              <motion.div
                className="flex items-center space-x-2"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Table className="h-4 w-4" />
                <span>Table</span>
              </motion.div>
            </Tabs.Trigger>
            <Tabs.Trigger
              value="visualizations"
              className="group relative flex items-center justify-center px-6 py-4 rounded-2xl font-semibold text-sm transition-all duration-500 ease-in-out transform data-[state=active]:bg-gradient-to-br data-[state=active]:from-purple-500 data-[state=active]:via-indigo-500 data-[state=active]:to-blue-600 data-[state=active]:text-white data-[state=active]:shadow-2xl data-[state=active]:scale-105 data-[state=inactive]:bg-white/60 data-[state=inactive]:text-gray-600 data-[state=inactive]:hover:bg-white/80 data-[state=inactive]:hover:scale-102 data-[state=inactive]:hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-purple-400 focus:ring-offset-2 focus:ring-offset-transparent"
            >
              <motion.div
                className="flex items-center space-x-2"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <PieChart className="h-4 w-4" />
                <span>Visualizations</span>
              </motion.div>
            </Tabs.Trigger>
            <Tabs.Trigger
              value="activity"
              className="group relative flex items-center justify-center px-6 py-4 rounded-2xl font-semibold text-sm transition-all duration-500 ease-in-out transform data-[state=active]:bg-gradient-to-br data-[state=active]:from-amber-500 data-[state=active]:via-orange-500 data-[state=active]:to-red-600 data-[state=active]:text-white data-[state=active]:shadow-2xl data-[state=active]:scale-105 data-[state=inactive]:bg-white/60 data-[state=inactive]:text-gray-600 data-[state=inactive]:hover:bg-white/80 data-[state=inactive]:hover:scale-102 data-[state=inactive]:hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-amber-400 focus:ring-offset-2 focus:ring-offset-transparent"
            >
              <motion.div
                className="flex items-center space-x-2"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Target className="h-4 w-4" />
                <span>Activity Tracker</span>
              </motion.div>
            </Tabs.Trigger>
          </Tabs.List>

          {/* Tab Content */}
          <Tabs.Content value="table" className="focus:outline-none">
            {/* Sales Rep Leaderboard Section */}
            {leaderboardMetrics.length > 0 && sortedSalesReps.length > 0 && (
              <motion.div
                className="mt-8"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                {/* Premium Leaderboard Table - Stripe-inspired with rich animations */}
                <div className="bg-white rounded-2xl border border-gray-200/60 shadow-xl hover:shadow-2xl transition-all duration-700 overflow-hidden backdrop-blur-sm">
                  {/* Subtle gradient overlay for depth */}
                  <div className="absolute inset-0 bg-gradient-to-br from-white via-gray-50/30 to-white opacity-60 pointer-events-none"></div>

                  <div className="relative">
                    <table className="w-full table-fixed">
                      <thead>
                        {/* Enhanced Header Row: Sales Rep Names */}
                        <motion.tr
                          className="bg-gradient-to-r from-gray-50 via-white to-gray-50 border-b border-gray-200/80 backdrop-blur-sm"
                          initial={{ opacity: 0, y: -20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.8, ease: 'easeOut' }}
                          whileHover={{
                            background:
                              'linear-gradient(to right, rgb(249 250 251), rgb(255 255 255), rgb(249 250 251))',
                            transition: { duration: 0.3 },
                          }}
                        >
                          <motion.th
                            className="px-6 py-4 text-left text-sm font-semibold text-gray-900 uppercase tracking-wide relative group w-1/3"
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.1, duration: 0.6 }}
                            whileHover={{
                              scale: 1.02,
                              transition: { type: 'spring', stiffness: 400, damping: 25 },
                            }}
                          >
                            <div className="flex items-center space-x-3 relative">
                              <motion.div
                                className="p-1.5 rounded-lg bg-gray-100 group-hover:bg-gray-200 transition-colors duration-300"
                                whileHover={{ rotate: 5, scale: 1.1 }}
                                transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                              >
                                <BarChart3 className="h-4 w-4 text-gray-700 group-hover:text-gray-900 transition-colors duration-300" />
                              </motion.div>
                              <span className="text-sm font-semibold group-hover:text-gray-700 transition-colors duration-300">
                                Metric
                              </span>
                              {/* Subtle underline effect on hover */}
                              <motion.div
                                className="absolute bottom-0 left-0 h-0.5 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"
                                initial={{ width: 0 }}
                                whileHover={{ width: '100%' }}
                                transition={{ duration: 0.3 }}
                              />
                            </div>
                          </motion.th>
                          {sortedSalesReps.map((repName, index) => (
                            <motion.th
                              key={repName}
                              className={`px-2 py-4 text-center text-sm font-semibold text-gray-900 uppercase tracking-wide relative group ${
                                sortedSalesReps.length === 3
                                  ? 'w-1/6'
                                  : sortedSalesReps.length === 4
                                    ? 'w-1/6'
                                    : 'w-1/5'
                              }`}
                              initial={{ opacity: 0, y: -20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: 0.2 + index * 0.1, duration: 0.6 }}
                              whileHover={{
                                scale: 1.05,
                                y: -2,
                                transition: { type: 'spring', stiffness: 400, damping: 25 },
                              }}
                            >
                              <div className="flex items-center justify-center space-x-3 relative">
                                <motion.div
                                  className="p-1.5 rounded-full bg-gradient-to-br from-blue-50 to-indigo-100 group-hover:from-blue-100 group-hover:to-indigo-200 transition-all duration-300 shadow-sm group-hover:shadow-md"
                                  whileHover={{ rotate: 360, scale: 1.15 }}
                                  transition={{ duration: 0.5 }}
                                >
                                  <User className="h-3.5 w-3.5 text-blue-600 group-hover:text-blue-700 transition-colors duration-300" />
                                </motion.div>
                                <span className="text-sm font-semibold group-hover:text-gray-700 transition-colors duration-300">
                                  {repName}
                                </span>
                                {/* Subtle glow effect on hover */}
                                <motion.div
                                  className="absolute inset-0 rounded-lg bg-gradient-to-r from-blue-500/10 to-purple-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 -z-10"
                                  initial={{ scale: 0.8 }}
                                  whileHover={{ scale: 1.05 }}
                                  transition={{ duration: 0.3 }}
                                />
                              </div>
                            </motion.th>
                          ))}
                        </motion.tr>
                      </thead>
                      <tbody className="bg-white divide-y divide-gray-100/50">
                        {/* Enhanced Dynamic Rows: One row per metric */}
                        {leaderboardMetrics.map((metric, metricIndex) => (
                          <motion.tr
                            key={metric.id}
                            className={`group relative transition-all duration-500 border-l-4 border-transparent hover:shadow-lg hover:shadow-${metric.color?.replace('#', '').toLowerCase()}-100/50 ${getMetricRowHoverBackground(metric.id)} ${getMetricBorderColor(metric.id)}`}
                            whileHover={{
                              scale: 1.008,
                              y: -1,
                              transition: { type: 'spring', stiffness: 300, damping: 25 },
                            }}
                            initial={{ opacity: 0, y: 15, x: -10 }}
                            animate={{ opacity: 1, y: 0, x: 0 }}
                            transition={{
                              delay: 0.1 + metricIndex * 0.08,
                              duration: 0.7,
                              ease: 'easeOut',
                            }}
                          >
                            <td className="px-4 py-4 whitespace-nowrap relative">
                              <div className="flex items-center space-x-3">
                                <motion.div
                                  className={`flex-shrink-0 h-10 w-10 flex items-center justify-center rounded-lg shadow-sm group-hover:shadow-md transition-all duration-300 ${getMetricIconBackground(metric.id)}`}
                                  whileHover={{
                                    scale: 1.15,
                                    rotate: 5,
                                    transition: { type: 'spring', stiffness: 400, damping: 20 },
                                  }}
                                >
                                  {(() => {
                                    const IconComponent = getMetricIcon(metric.id);

                                    return (
                                      <IconComponent
                                        className={`h-5 w-5 ${getMetricIconColor(metric.id)}`}
                                      />
                                    );
                                  })()}
                                </motion.div>
                                <div className="flex-1">
                                  <div className="flex items-center gap-1">
                                    <motion.span
                                      className="text-sm font-bold text-slate-800 group-hover:text-gray-700 transition-colors duration-300 truncate"
                                      whileHover={{ x: 2 }}
                                      transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                                    >
                                      {metric.name}
                                    </motion.span>
                                    <MetricInfoTooltip info={metric.info} query={metric.query} />
                                  </div>
                                  {/* Subtle progress indicator */}
                                  <motion.div
                                    className="h-0.5 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full mt-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                                    initial={{ width: 0 }}
                                    whileHover={{ width: '60%' }}
                                    transition={{ duration: 0.4, delay: 0.1 }}
                                  />
                                </div>
                              </div>
                            </td>
                            {sortedSalesReps.map((repName, repIndex) => {
                              const metricValue = metric.value as
                                | { [key: string]: number | string }
                                | undefined;
                              const rawValue = metricValue?.[repName];

                              // Handle combined adoption metrics (string format: "count (rate%)")
                              const isCombinedAdoption = metric.id.includes('-adoption-combined-');

                              return (
                                <motion.td
                                  key={repName}
                                  className="px-2 py-3 whitespace-nowrap text-center relative group/cell"
                                  initial={{ opacity: 0, scale: 0.9, y: 10 }}
                                  animate={{ opacity: 1, scale: 1, y: 0 }}
                                  transition={{
                                    delay: 0.15 + metricIndex * 0.06 + repIndex * 0.03,
                                    duration: 0.6,
                                    type: 'spring',
                                    stiffness: 300,
                                    damping: 25,
                                  }}
                                  whileHover={{
                                    scale: 1.02,
                                    transition: { type: 'spring', stiffness: 400, damping: 25 },
                                  }}
                                >
                                  <motion.div
                                    className="relative inline-block"
                                    whileHover={{
                                      scale: 1.08,
                                      transition: { type: 'spring', stiffness: 400, damping: 20 },
                                    }}
                                  >
                                    {/* Background highlight on hover */}
                                    <motion.div
                                      className={`absolute inset-0 bg-gradient-to-r ${getMetricCellHoverBackground(metric.id)} rounded-lg opacity-0 group-hover/cell:opacity-100 transition-opacity duration-300 -z-10`}
                                      initial={{ scale: 0.8 }}
                                      whileHover={{ scale: 1.1 }}
                                      transition={{ duration: 0.2 }}
                                    />
                                    <span className="text-sm font-bold text-slate-800 group-hover/cell:text-gray-700 transition-colors duration-300">
                                      {isCombinedAdoption
                                        ? (rawValue as string) || '0 (0.0%)'
                                        : (() => {
                                            const value = (rawValue as number) || 0;

                                            return metric.displayType === 'currency'
                                              ? formatCurrency(value)
                                              : metric.id === 'sales-ehr-close-rate-leaderboard'
                                                ? `${value.toFixed(1)}%`
                                                : metric.id ===
                                                    'sales-ehr-avg-sales-cycle-leaderboard'
                                                  ? `${value.toFixed(1)} days`
                                                  : metric.id ===
                                                      'sales-ehr-annual-contract-value-leaderboard'
                                                    ? formatCurrency(value)
                                                    : metric.id ===
                                                        'sales-ehr-avg-annual-deal-leaderboard'
                                                      ? formatCurrency(value)
                                                      : formatNumber(value);
                                          })()}
                                    </span>
                                  </motion.div>
                                </motion.td>
                              );
                            })}
                          </motion.tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Enhanced Summary Footer */}
                  <motion.div
                    className="px-6 py-4 bg-gradient-to-r from-emerald-50/80 via-green-50/60 to-teal-50/80 border-t border-emerald-200/50 backdrop-blur-sm"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8, duration: 0.5 }}
                  >
                    <div className="flex justify-between items-center text-sm">
                      <motion.span
                        className="flex items-center space-x-2 text-emerald-700 font-medium"
                        whileHover={{ scale: 1.05 }}
                      >
                        <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                        <span>
                          Total Metrics:{' '}
                          <strong className="text-emerald-800">{leaderboardMetrics.length}</strong>
                        </span>
                      </motion.span>
                      <motion.span
                        className="flex items-center space-x-2 text-emerald-700 font-medium"
                        whileHover={{ scale: 1.05 }}
                      >
                        <Trophy className="h-3 w-3 text-emerald-600" />
                        <span>
                          Active Sales Reps:{' '}
                          <strong className="text-emerald-800">{sortedSalesReps.length}</strong>
                        </span>
                      </motion.span>
                    </div>
                  </motion.div>
                </div>
              </motion.div>
            )}
          </Tabs.Content>

          {/* Visualizations Tab Content */}
          <Tabs.Content value="visualizations" className="focus:outline-none">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              {/* Performance Gauges Section */}
              <div className="mb-8">
                <motion.h3
                  className="text-xl font-bold text-gray-900 mb-6 text-center"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2, duration: 0.6 }}
                >
                  Sales Rep Close Rate Performance
                </motion.h3>

                {/* Performance Gauges Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {sortedSalesReps.map((repName, index) => {
                    // Calculate close rate using the same formula as the table
                    const closedSalesMetric = leaderboardMetrics.find(
                      m => m.id === 'sales-ehr-closed-sales-leaderboard'
                    );
                    const newDemosMetric = leaderboardMetrics.find(
                      m => m.id === 'sales-ehr-new-demos-leaderboard'
                    );

                    const closedSales =
                      (closedSalesMetric?.value as { [key: string]: number })?.[repName] || 0;
                    const newDemos =
                      (newDemosMetric?.value as { [key: string]: number })?.[repName] || 0;

                    // Same calculation as table: (closed sales / new demos) * 100
                    const closeRate = newDemos > 0 ? (closedSales / newDemos) * 100 : 0;

                    // Determine performance level and colors
                    const getPerformanceLevel = (rate: number) => {
                      if (rate >= 50) {
                        return {
                          level: 'Optimal',
                          color: '#10B981',
                          bgColor: 'from-green-500 to-emerald-500',
                        };
                      }

                      if (rate >= 30) {
                        return {
                          level: 'Good',
                          color: '#22C55E',
                          bgColor: 'from-lime-500 to-green-400',
                        };
                      }

                      if (rate >= 20) {
                        return {
                          level: 'Fair',
                          color: '#F59E0B',
                          bgColor: 'from-yellow-500 to-orange-500',
                        };
                      }

                      return {
                        level: 'Pay Attention',
                        color: '#DC2626',
                        bgColor: 'from-red-500 to-red-600',
                      };
                    };

                    const performance = getPerformanceLevel(closeRate);

                    return (
                      <motion.div
                        key={repName}
                        className="bg-white rounded-2xl border border-gray-200/60 shadow-lg hover:shadow-xl transition-all duration-500 p-6"
                        initial={{ opacity: 0, y: 30, scale: 0.9 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        transition={{ delay: 0.3 + index * 0.2, duration: 0.6 }}
                        whileHover={{ scale: 1.02, y: -5 }}
                      >
                        {/* Sales Rep Name */}
                        <div className="text-center mb-6">
                          <motion.div
                            className="w-12 h-12 mx-auto mb-3 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full flex items-center justify-center shadow-md"
                            whileHover={{ scale: 1.1, rotate: 5 }}
                            transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                          >
                            <User className="h-6 w-6 text-blue-600" />
                          </motion.div>
                          <h4 className="text-lg font-bold text-gray-900">{repName}</h4>
                          <p className="text-sm text-gray-500">Close Rate Performance</p>
                        </div>

                        {/* Dynamic SVG Gauge with Enhanced Animations */}
                        <div className="relative flex items-center justify-center mb-6">
                          <motion.svg
                            width="200"
                            height="120"
                            viewBox="0 0 200 120"
                            className="overflow-visible"
                            whileHover={{ scale: 1.05 }}
                            transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                          >
                            {/* Background Arc with subtle animation */}
                            <motion.path
                              d="M 20 100 A 80 80 0 0 1 180 100"
                              fill="none"
                              stroke="#E5E7EB"
                              strokeWidth="12"
                              strokeLinecap="round"
                              initial={{ pathLength: 0 }}
                              animate={{ pathLength: 1 }}
                              transition={{ duration: 1, delay: 0.2 + index * 0.1 }}
                            />

                            {/* Performance Arc - Clean and Simple */}
                            <path
                              d="M 20 100 A 80 80 0 0 1 180 100"
                              fill="none"
                              stroke={`url(#gradient-${index})`}
                              strokeWidth="12"
                              strokeLinecap="round"
                              strokeDasharray={`${(closeRate / 100) * 251.33} 251.33`}
                            />

                            {/* Simple gradient definition */}
                            <defs>
                              <linearGradient
                                id={`gradient-${index}`}
                                x1="0%"
                                y1="0%"
                                x2="100%"
                                y2="0%"
                              >
                                <stop offset="0%" stopColor={performance.color} stopOpacity="0.8" />
                                <stop offset="50%" stopColor={performance.color} stopOpacity="1" />
                                <stop
                                  offset="100%"
                                  stopColor={performance.color}
                                  stopOpacity="0.8"
                                />
                              </linearGradient>

                              {/* Glow effect filter */}
                              <filter id={`glow-${index}`}>
                                <feGaussianBlur stdDeviation="3" result="coloredBlur" />
                                <feMerge>
                                  <feMergeNode in="coloredBlur" />
                                  <feMergeNode in="SourceGraphic" />
                                </feMerge>
                              </filter>
                            </defs>

                            {/* Animated center text with counter effect */}
                            <motion.text
                              x="100"
                              y="85"
                              textAnchor="middle"
                              className="fill-gray-900 text-2xl font-bold"
                              initial={{ scale: 0, opacity: 0 }}
                              animate={{ scale: 1, opacity: 1 }}
                              transition={{ delay: 2 + index * 0.3, duration: 0.5, type: 'spring' }}
                            >
                              <motion.tspan
                                animate={{
                                  scale: [1, 1.1, 1],
                                  fill: [performance.color, '#1F2937', performance.color],
                                }}
                                transition={{
                                  duration: 2,
                                  repeat: Infinity,
                                  repeatType: 'reverse',
                                  ease: 'easeInOut',
                                }}
                              >
                                {closeRate.toFixed(1)}%
                              </motion.tspan>
                            </motion.text>
                            <motion.text
                              x="100"
                              y="102"
                              textAnchor="middle"
                              className="fill-gray-500 text-sm"
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: 2.2 + index * 0.3, duration: 0.5 }}
                            >
                              Close Rate
                            </motion.text>
                          </motion.svg>
                        </div>

                        {/* Performance Level Badge */}
                        <div className="text-center mb-4">
                          <motion.div
                            className={`inline-flex items-center space-x-2 px-4 py-2 bg-gradient-to-r ${performance.bgColor} text-white rounded-full font-semibold text-sm shadow-lg`}
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 1 + index * 0.2, duration: 0.5 }}
                            whileHover={{ scale: 1.05 }}
                          >
                            <Trophy className="h-4 w-4" />
                            <span>{performance.level}</span>
                          </motion.div>
                        </div>

                        {/* Stats */}
                        <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-100">
                          <div className="text-center">
                            <motion.div
                              className="text-2xl font-bold text-gray-900"
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: 1.2 + index * 0.2, duration: 0.5 }}
                            >
                              {closedSales}
                            </motion.div>
                            <div className="text-xs text-gray-500 mt-1">Closed Sales</div>
                          </div>
                          <div className="text-center">
                            <motion.div
                              className="text-2xl font-bold text-gray-900"
                              initial={{ opacity: 0, y: 10 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ delay: 1.4 + index * 0.2, duration: 0.5 }}
                            >
                              {newDemos}
                            </motion.div>
                            <div className="text-xs text-gray-500 mt-1">New Demos</div>
                          </div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </div>

              {/* Deal Velocity Dashboard */}
              <div className="mb-8">
                <motion.h3
                  className="text-xl font-bold text-gray-900 mb-6 text-center"
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2, duration: 0.6 }}
                >
                  Deal Velocity Dashboard
                </motion.h3>

                {/* Compact Velocity Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  {sortedSalesReps.map((repName, index) => {
                    // Get sales cycle length data from the table
                    const salesCycleMetric = leaderboardMetrics.find(
                      m => m.id === 'sales-ehr-avg-sales-cycle-leaderboard'
                    );
                    const avgDays =
                      (salesCycleMetric?.value as { [key: string]: number })?.[repName] || 0;

                    // Determine velocity colors and descriptions based on days
                    const getVelocityInfo = (days: number) => {
                      if (days === 0) {
                        return {
                          color: '#9CA3AF',
                          bgColor: 'from-gray-400 to-gray-500',
                          label: 'No Data',
                        };
                      }

                      if (days < 45) {
                        return {
                          color: '#10B981',
                          bgColor: 'from-green-500 to-emerald-500',
                          label: 'Optimal',
                        };
                      }

                      if (days >= 45 && days < 60) {
                        return {
                          color: '#22C55E',
                          bgColor: 'from-lime-500 to-green-400',
                          label: 'Good',
                        };
                      }

                      if (days >= 60 && days < 75) {
                        return {
                          color: '#F59E0B',
                          bgColor: 'from-yellow-500 to-orange-500',
                          label: 'Fair',
                        };
                      }

                      return {
                        color: '#EF4444',
                        bgColor: 'from-red-500 to-red-600',
                        label: 'Pay Attention',
                      };
                    };

                    const velocity = getVelocityInfo(avgDays);

                    // Get closed sales count
                    const closedSalesMetric = leaderboardMetrics.find(
                      m => m.id === 'sales-ehr-closed-sales-leaderboard'
                    );
                    const closedSales =
                      (closedSalesMetric?.value as { [key: string]: number })?.[repName] || 0;

                    // Calculate bar width (inverse of days - faster = wider bar)
                    const maxDays = 90; // Assume 90 days is the baseline
                    const barWidth =
                      avgDays > 0 ? Math.max(20, 100 - (avgDays / maxDays) * 80) : 20;

                    return (
                      <motion.div
                        key={repName}
                        className="bg-white rounded-2xl border border-gray-200/60 shadow-lg hover:shadow-xl transition-all duration-500 p-6"
                        initial={{ opacity: 0, y: 30, scale: 0.9 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        transition={{ delay: 0.3 + index * 0.2, duration: 0.6 }}
                        whileHover={{ scale: 1.02, y: -5 }}
                      >
                        {/* Sales Rep Name */}
                        <div className="text-center mb-4">
                          <motion.div
                            className="w-12 h-12 mx-auto mb-3 bg-gradient-to-br from-blue-100 to-indigo-100 rounded-full flex items-center justify-center shadow-md"
                            whileHover={{ scale: 1.1, rotate: 5 }}
                            transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                          >
                            <User className="h-6 w-6 text-blue-600" />
                          </motion.div>
                          <h4 className="text-lg font-bold text-gray-900">{repName}</h4>
                          <p className="text-sm font-medium" style={{ color: velocity.color }}>
                            {velocity.label}
                          </p>
                        </div>

                        {/* Days Badge */}
                        <div className="text-center mb-4">
                          <motion.div
                            className={`inline-flex items-center space-x-2 px-4 py-2 bg-gradient-to-r ${velocity.bgColor} text-white rounded-full font-semibold text-sm shadow-lg`}
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 0.8 + index * 0.2, duration: 0.5 }}
                            whileHover={{ scale: 1.05 }}
                          >
                            <Clock className="h-4 w-4" />
                            <span>{avgDays > 0 ? `${avgDays.toFixed(1)} days` : 'No data'}</span>
                          </motion.div>
                        </div>

                        {/* Compact Progress Bar */}
                        <div className="mb-4">
                          <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden shadow-inner">
                            <motion.div
                              className={`h-full bg-gradient-to-r ${velocity.bgColor} rounded-full shadow-sm relative overflow-hidden`}
                              initial={{ width: 0 }}
                              animate={{ width: `${barWidth}%` }}
                              transition={{
                                duration: 1.5,
                                delay: 1 + index * 0.3,
                                ease: 'easeOut',
                              }}
                            >
                              {/* Shimmer effect */}
                              <motion.div
                                className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent"
                                animate={{ x: ['-100%', '100%'] }}
                                transition={{
                                  duration: 2,
                                  repeat: Infinity,
                                  delay: 1.5 + index * 0.3,
                                  ease: 'easeInOut',
                                }}
                              />
                            </motion.div>
                          </div>
                        </div>

                        {/* Closed Deals Count */}
                        <div className="text-center pt-4 border-t border-gray-100">
                          <motion.div
                            className="text-2xl font-bold text-gray-900"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 1.5 + index * 0.2, duration: 0.5 }}
                          >
                            {closedSales}
                          </motion.div>
                          <div className="text-sm text-gray-500 mt-1">Closed Deals</div>
                        </div>
                      </motion.div>
                    );
                  })}
                </div>

                {/* Enhanced Velocity Insights */}
                <motion.div
                  className="mt-8 bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20 rounded-3xl border border-slate-200/60 shadow-lg hover:shadow-xl transition-all duration-500 p-8"
                  initial={{ opacity: 0, y: 30, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ delay: 1.5, duration: 0.8, ease: 'easeOut' }}
                  whileHover={{ scale: 1.01, y: -2 }}
                >
                  {/* Header */}
                  <motion.div
                    className="flex items-center justify-center space-x-3 mb-6"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.7, duration: 0.6 }}
                  >
                    <motion.div
                      className="w-10 h-10 bg-gradient-to-br from-blue-500 via-indigo-500 to-purple-500 rounded-xl flex items-center justify-center shadow-lg"
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                    >
                      <TrendingUp className="h-5 w-5 text-white" />
                    </motion.div>
                    <div className="text-center">
                      <h4 className="text-xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
                        Velocity Insights
                      </h4>
                      <p className="text-xs text-gray-500 mt-0.5">Team performance at a glance</p>
                    </div>
                  </motion.div>

                  {/* Insights Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Speed Champion Card */}
                    <motion.div
                      className="group relative bg-white/80 backdrop-blur-sm rounded-xl border border-green-200/50 p-4 hover:bg-white hover:border-green-400/70 transition-all duration-500 shadow-sm hover:shadow-xl cursor-pointer overflow-hidden"
                      initial={{ opacity: 0, y: 20, scale: 0.9 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      transition={{ delay: 1.9, duration: 0.6 }}
                      whileHover={{
                        y: -8,
                        scale: 1.03,
                        transition: { type: 'spring', stiffness: 400, damping: 25 },
                      }}
                    >
                      <div className="text-center">
                        <motion.div
                          className="w-12 h-12 mx-auto mb-3 bg-gradient-to-br from-green-400 to-emerald-500 rounded-xl flex items-center justify-center shadow-md"
                          whileHover={{ scale: 1.1, rotate: 10 }}
                          transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                        >
                          <Zap className="h-6 w-6 text-white" />
                        </motion.div>
                        <h5 className="text-base font-bold text-green-700 mb-1">Speed Champion</h5>
                        <div className="text-lg font-bold text-gray-800 mb-0.5">
                          {sortedSalesReps.reduce((fastest, rep) => {
                            const salesCycleMetric = leaderboardMetrics.find(
                              m => m.id === 'sales-ehr-avg-sales-cycle-leaderboard'
                            );
                            const repDays =
                              (salesCycleMetric?.value as { [key: string]: number })?.[rep] || 999;
                            const fastestDays =
                              (salesCycleMetric?.value as { [key: string]: number })?.[fastest] ||
                              999;

                            return repDays < fastestDays && repDays > 0 ? rep : fastest;
                          }, sortedSalesReps[0])}
                        </div>
                        <p className="text-xs text-gray-500">Fastest closer</p>
                      </div>
                      {/* Enhanced glow and shimmer effects */}
                      <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-green-400/15 to-emerald-500/15 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
                      <motion.div
                        className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                        initial={{ x: '-100%' }}
                        whileHover={{ x: '100%' }}
                        transition={{ duration: 0.6, ease: 'easeInOut' }}
                      />
                      <div className="absolute inset-0 rounded-xl border border-green-300/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    </motion.div>

                    {/* Average Cycle Card */}
                    <motion.div
                      className="group relative bg-white/80 backdrop-blur-sm rounded-xl border border-blue-200/50 p-4 hover:bg-white hover:border-blue-400/70 transition-all duration-500 shadow-sm hover:shadow-xl cursor-pointer overflow-hidden"
                      initial={{ opacity: 0, y: 20, scale: 0.9 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      transition={{ delay: 2.1, duration: 0.6 }}
                      whileHover={{
                        y: -8,
                        scale: 1.03,
                        transition: { type: 'spring', stiffness: 400, damping: 25 },
                      }}
                    >
                      <div className="text-center">
                        <motion.div
                          className="w-12 h-12 mx-auto mb-3 bg-gradient-to-br from-blue-400 to-indigo-500 rounded-xl flex items-center justify-center shadow-md"
                          whileHover={{ scale: 1.1, rotate: -10 }}
                          transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                        >
                          <BarChart3 className="h-6 w-6 text-white" />
                        </motion.div>
                        <h5 className="text-base font-bold text-blue-700 mb-1">Average Cycle</h5>
                        <div className="text-lg font-bold text-gray-800 mb-0.5">
                          {(() => {
                            const salesCycleMetric = leaderboardMetrics.find(
                              m => m.id === 'sales-ehr-avg-sales-cycle-leaderboard'
                            );
                            const allDays = sortedSalesReps
                              .map(
                                rep =>
                                  (salesCycleMetric?.value as { [key: string]: number })?.[rep] || 0
                              )
                              .filter(d => d > 0);
                            const avg =
                              allDays.length > 0
                                ? allDays.reduce((a, b) => a + b, 0) / allDays.length
                                : 0;

                            return avg > 0 ? `${avg.toFixed(1)} days` : 'No data';
                          })()}
                        </div>
                        <p className="text-xs text-gray-500">Team average</p>
                      </div>
                      {/* Enhanced glow and shimmer effects */}
                      <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-blue-400/15 to-indigo-500/15 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
                      <motion.div
                        className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                        initial={{ x: '-100%' }}
                        whileHover={{ x: '100%' }}
                        transition={{ duration: 0.6, ease: 'easeInOut' }}
                      />
                      <div className="absolute inset-0 rounded-xl border border-blue-300/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    </motion.div>

                    {/* Opportunity Card */}
                    <motion.div
                      className="group relative bg-white/80 backdrop-blur-sm rounded-xl border border-orange-200/50 p-4 hover:bg-white hover:border-orange-400/70 transition-all duration-500 shadow-sm hover:shadow-xl cursor-pointer overflow-hidden"
                      initial={{ opacity: 0, y: 20, scale: 0.9 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      transition={{ delay: 2.3, duration: 0.6 }}
                      whileHover={{
                        y: -8,
                        scale: 1.03,
                        transition: { type: 'spring', stiffness: 400, damping: 25 },
                      }}
                    >
                      <div className="text-center">
                        <motion.div
                          className="w-12 h-12 mx-auto mb-3 bg-gradient-to-br from-orange-400 to-red-500 rounded-xl flex items-center justify-center shadow-md"
                          whileHover={{ scale: 1.1, rotate: 5 }}
                          transition={{ type: 'spring', stiffness: 400, damping: 20 }}
                        >
                          <Target className="h-6 w-6 text-white" />
                        </motion.div>
                        <h5 className="text-base font-bold text-orange-700 mb-1">Opportunity</h5>
                        <div className="text-sm font-semibold text-gray-800 mb-0.5 leading-tight">
                          {(() => {
                            // Calculate dynamic opportunity based on current average cycle time
                            const salesCycleMetric = leaderboardMetrics.find(
                              m => m.id === 'sales-ehr-avg-sales-cycle-leaderboard'
                            );
                            const allDays = sortedSalesReps
                              .map(
                                rep =>
                                  (salesCycleMetric?.value as { [key: string]: number })?.[rep] || 0
                              )
                              .filter(d => d > 0);
                            const currentAvg =
                              allDays.length > 0
                                ? allDays.reduce((a, b) => a + b, 0) / allDays.length
                                : 0;

                            if (currentAvg === 0) {
                              return (
                                <>
                                  Optimize cycle time
                                  <br />
                                  <span className="text-base text-orange-600">for growth</span>
                                </>
                              );
                            }

                            // Calculate optimal reduction (15-25% of current cycle)
                            const reductionPercentage =
                              currentAvg > 60 ? 0.25 : currentAvg > 40 ? 0.2 : 0.15;
                            const targetReduction = Math.round(currentAvg * reductionPercentage);

                            // Calculate potential deal increase using sales velocity formula
                            // Formula: Deal increase % = (Cycle reduction % / (1 - Cycle reduction %)) * 100
                            const dealIncrease = Math.round(
                              (reductionPercentage / (1 - reductionPercentage)) * 100
                            );

                            return (
                              <>
                                Reduce cycle by
                                <br />
                                <span className="text-base text-orange-600">
                                  {targetReduction} days
                                </span>
                              </>
                            );
                          })()}
                        </div>
                        <p className="text-xs text-gray-500">
                          {(() => {
                            // Calculate the percentage increase for display
                            const salesCycleMetric = leaderboardMetrics.find(
                              m => m.id === 'sales-ehr-avg-sales-cycle-leaderboard'
                            );
                            const allDays = sortedSalesReps
                              .map(
                                rep =>
                                  (salesCycleMetric?.value as { [key: string]: number })?.[rep] || 0
                              )
                              .filter(d => d > 0);
                            const currentAvg =
                              allDays.length > 0
                                ? allDays.reduce((a, b) => a + b, 0) / allDays.length
                                : 0;

                            if (currentAvg === 0) return '= More opportunities';

                            const reductionPercentage =
                              currentAvg > 60 ? 0.25 : currentAvg > 40 ? 0.2 : 0.15;
                            const dealIncrease = Math.round(
                              (reductionPercentage / (1 - reductionPercentage)) * 100
                            );

                            return `= ${dealIncrease}% more deals`;
                          })()}
                        </p>
                      </div>
                      {/* Enhanced glow and shimmer effects */}
                      <div className="absolute inset-0 rounded-xl bg-gradient-to-r from-orange-400/15 to-red-500/15 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" />
                      <motion.div
                        className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                        initial={{ x: '-100%' }}
                        whileHover={{ x: '100%' }}
                        transition={{ duration: 0.6, ease: 'easeInOut' }}
                      />
                      <div className="absolute inset-0 rounded-xl border border-orange-300/30 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                    </motion.div>
                  </div>

                  {/* Bottom accent line */}
                  <motion.div
                    className="mt-4 h-0.5 bg-gradient-to-r from-green-400 via-blue-500 to-orange-400 rounded-full opacity-30"
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ delay: 2.5, duration: 1, ease: 'easeOut' }}
                  />
                </motion.div>
              </div>

              {/* Sales Trend Over Time Chart */}
              <motion.div
                className="mt-8"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.6 }}
              >
                {(() => {
                  const trendMetric = allMetrics.find(m => m.id === 'sales-trend-over-time');

                  if (isLoading || isFetching) {
                    return (
                      <div className="flex items-center justify-center py-12 bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700">
                        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
                      </div>
                    );
                  }

                  if (
                    !trendMetric ||
                    typeof trendMetric.value !== 'object' ||
                    !trendMetric.value ||
                    !(trendMetric.value as any).data
                  ) {
                    return (
                      <div className="flex items-center justify-center py-12 bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700">
                        <div className="text-center">
                          <TrendingUp className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                          <p className="text-gray-500 dark:text-gray-400">
                            No trend data available
                          </p>
                        </div>
                      </div>
                    );
                  }

                  const trendData = (trendMetric.value as any).data;
                  const rawRecords = (trendMetric.value as any).records || [];

                  // Always show these specific sales reps (matching table filter)
                  const salesReps = ['Becki Wollenburg', 'Curt Langel', 'Erika Roland'];

                  return (
                    <SalesTrendLineChart
                      data={trendData}
                      title="Sales Trend Over Time"
                      info={trendMetric.info}
                      query={trendMetric.query}
                      rawRecords={rawRecords}
                      salesReps={salesReps}
                    />
                  );
                })()}
              </motion.div>
            </motion.div>
          </Tabs.Content>

          {/* Activity Tracker Tab Content */}
          <Tabs.Content value="activity" className="focus:outline-none">
            {activityQuery.isLoading || (activityQuery.isFetching && !activityQuery.data) ? (
              <div className="flex items-center justify-center py-20">
                <div className="flex flex-col items-center gap-3">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
                  <p className="text-sm text-gray-500">Loading activity…</p>
                </div>
              </div>
            ) : (
              <motion.div
                className="mt-8"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                {renderActivityTable(
                  'Sales Managers',
                  activityRows.filter(row => row.role === 'manager'),
                  true
                )}
                {renderActivityTable(
                  'BDRs',
                  activityRows.filter(row => row.role === 'bdr'),
                  false
                )}
              </motion.div>
            )}
          </Tabs.Content>
        </Tabs.Root>
      </div>
    </div>
  );
};

export default SalesDashboard;
